/**
 * Pager for fastapi-pagination `Page<T>` responses.
 *
 * The S2S and grounding event endpoints return results sorted oldest-first, so
 * fetching only page 1 silently drops the most recent events in a timeframe.
 * These hooks must walk every page.
 *
 * Page 1 is fetched first to learn the page count; the remaining pages are then
 * fetched in bounded-concurrency batches and reassembled in order, so a wide
 * timeframe spanning many pages loads far faster than a strictly sequential walk.
 *
 * `fetchPage` MUST be safe to call concurrently: it must not depend on shared
 * mutable state that a later page would overwrite before an earlier page reads it.
 */

/** Minimal shape shared by all fastapi-pagination `Page<T>` responses. */
export interface PaginatedResponse<T> {
  items?: T[] | null;
  /** Total number of pages; null/undefined is treated as a single page. */
  pages?: number | null;
  total?: number | null;
}

/** Page size requested per page (fastapi-pagination's default maximum). */
export const EVENTS_PAGE_SIZE = 100;

/** Safety cap on pages fetched per query (~5,000 events at EVENTS_PAGE_SIZE). */
export const MAX_EVENT_PAGES = 50;

/**
 * Max page requests in flight at once. Browsers already cap connections per host
 * (~6), so this keeps pace with that ceiling without flooding the API gateway.
 */
const PAGE_FETCH_CONCURRENCY = 6;

interface FetchAllPagesOptions {
  /** Max pages to fetch before stopping and warning. Defaults to MAX_EVENT_PAGES. */
  maxPages?: number;
  /** Label used in the cap-exceeded warning. */
  label?: string;
}

/** Runs `task` for every item, at most `limit` in flight, preserving input order. */
async function mapInBatches<I, O>(
  items: I[],
  limit: number,
  task: (item: I) => Promise<O>,
): Promise<O[]> {
  const results: O[] = [];
  for (let start = 0; start < items.length; start += limit) {
    const batch = items.slice(start, start + limit);
    const settled = await Promise.all(batch.map((item) => task(item)));
    results.push(...settled);
  }
  return results;
}

/**
 * Fetches every page from `fetchPage` (1-indexed) and returns a single response
 * with all items concatenated in page order. Page 1 is fetched first to learn
 * the page count, then the rest are fetched concurrently. Stops at `maxPages`,
 * logging a warning when the timeframe spans more pages than the cap.
 */
export async function fetchAllPages<T, P extends PaginatedResponse<T>>(
  fetchPage: (page: number) => Promise<P>,
  options: FetchAllPagesOptions = {},
): Promise<P> {
  const maxPages = options.maxPages ?? MAX_EVENT_PAGES;

  const firstPage = await fetchPage(1);
  const totalPages = firstPage.pages ?? 1;
  const pagesToFetch = Math.min(totalPages, maxPages);

  const remainingPages: number[] = [];
  for (let page = 2; page <= pagesToFetch; page++) {
    remainingPages.push(page);
  }
  const restPages = await mapInBatches(
    remainingPages,
    PAGE_FETCH_CONCURRENCY,
    fetchPage,
  );

  const items: T[] = [...(firstPage.items ?? [])];
  for (const nextPage of restPages) {
    items.push(...(nextPage.items ?? []));
  }

  if (totalPages > maxPages) {
    const prefix = options.label ? `${options.label}: ` : '';
    console.warn(
      `${prefix}timeframe spans ${totalPages} pages but is capped at ${maxPages}; ` +
        `showing the first ${items.length} of ${firstPage.total ?? 'unknown'} items.`,
    );
  }

  // Reconstruct the same page shape with every item merged into one page.
  return { ...firstPage, items, page: 1, size: items.length, pages: 1 } as P;
}
